import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Toast } from 'antd-mobile';
import { getMySupervisorScenarios, SupervisorScenarioInfo } from '@/lib/api/ai-supervisor';

interface ScenarioSelectorProps {
  onScenarioSelect: (scenarioId: string) => void;
  loading?: boolean;
}

const ScenarioSelector: React.FC<ScenarioSelectorProps> = ({ onScenarioSelect, loading: externalLoading }) => {
  const [scenarioList, setScenarioList] = useState<SupervisorScenarioInfo[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  // 获取进行中的督导场景列表
  const fetchScenarioList = async () => {
    setLoading(true);
    try {
      const response = await getMySupervisorScenarios({
        page: 1,
        pageSize: 100
      });

      if (response.success && response.data) {
        setScenarioList(response.data.list || []);
      } else {
        Toast.show({ content: '获取督导场景列表失败', icon: 'fail' });
      }
    } catch (error) {
      console.error('获取督导场景列表失败:', error);
      Toast.show({ content: '获取督导场景列表失败', icon: 'fail' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchScenarioList();
  }, []);

  if (loading || externalLoading) {
    return (
      <div className='flex items-center justify-center py-20'>
        <div className='text-[#909399] text-sm'>加载中...</div>
      </div>
    );
  }

  if (scenarioList.length === 0) {
    return (
      <div className='flex flex-col items-center justify-center py-20'>
        <div className='text-[#909399] text-sm'>暂无督导场景</div>
      </div>
    );
  }

  return (
    <div className='min-h-screen bg-gradient-to-b from-[#f8f9ff] to-[#ffffff]'>
      <div className='max-w-4xl mx-auto p-5'>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {/* 标题 */}
          <div className='text-[#303133] text-lg leading-6 text-left font-medium mb-4'>
            请选择督导场景
          </div>

          {/* 场景列表 */}
          <div className='space-y-3'>
            {scenarioList.map((scenario, index) => (
              <motion.div
                key={scenario.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
                onClick={() => onScenarioSelect(scenario.id)}
                className='bg-white rounded-2xl p-4 cursor-pointer active:scale-98 transition-transform'
                style={{
                  boxShadow: '0px 2px 8px 0px rgba(0, 0, 0, 0.04)'
                }}
              >
                <div className='flex items-start justify-between mb-3'>
                  <div className='flex items-center gap-3 flex-1'>
                    {/* 图标 */}
                    <div className='w-10 h-10 rounded-full bg-gradient-to-br from-[#a782ff] to-[#828eff] flex items-center justify-center flex-shrink-0'>
                      <svg width='20' height='20' viewBox='0 0 20 20' fill='none'>
                        <path d='M10 2v16M2 10h16' stroke='white' strokeWidth='2' strokeLinecap='round'/>
                      </svg>
                    </div>

                    {/* 场景信息 */}
                    <div className='flex-1 min-w-0'>
                      <div className='text-[#303133] text-base leading-6 font-medium mb-1'>
                        {scenario.name}
                      </div>
                      <div className='text-[#909399] text-xs leading-5 line-clamp-2'>
                        {scenario.description || '暂无描述'}
                      </div>
                    </div>
                  </div>

                  {/* 右箭头 */}
                  <svg width='24' height='24' viewBox='0 0 24 24' fill='none' className='flex-shrink-0 ml-2'>
                    <path d='M9 18l6-6-6-6' stroke='#909399' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'/>
                  </svg>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ScenarioSelector;
